import { DEFAULT_DATE_FORMAT } from '@aslanpay/portal-common-lib';
import {
  getHours,
  isAfter,
  isBefore,
  isMatch,
  isSameDay,
  isValid,
  parse,
  parseISO,
} from 'date-fns';

import { CountryCode, postcodeValidator } from 'postcode-validator';

export const validateEmail = (value: string): boolean => {
  if (value === undefined || value === null) return false;

  // Basic email format validation with stricter rules
  // Local part: 1-64 characters, cannot start/end with hyphen or dot, no consecutive dots
  // Domain part: valid domain with TLD (2-63 characters)
  const emailRegex =
    /^[a-zA-Z0-9]([a-zA-Z0-9._%+-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?\.[a-zA-Z]{2,63}$/;

  // Additional checks for edge cases
  if (!emailRegex.test(value)) return false;

  // Check for consecutive dots
  if (value.includes('..')) return false;

  // Check for consecutive hyphens
  if (value.includes('--')) return false;

  // Check total length
  if (value.length > 254) return false;

  // Split email into local and domain parts
  const [localPart, domainPart] = value.split('@');

  // Local part validation
  if (localPart.length === 0 || localPart.length > 64) return false;
  if (localPart.startsWith('-') || localPart.endsWith('-')) return false;
  if (localPart.startsWith('.') || localPart.endsWith('.')) return false;

  // Domain part validation
  if (domainPart.length === 0 || domainPart.length > 253) return false;
  if (domainPart.startsWith('-') || domainPart.endsWith('-')) return false;
  if (domainPart.startsWith('.') || domainPart.endsWith('.')) return false;

  return true;
};

export const validateDate = (value: string): boolean => {
  if (value === undefined) return false;

  if (isValid(parseISO(value)) && isMatch(value, 'yyyy-MM-dd')) {
    return true;
  }

  if (
    isValid(parse(value, DEFAULT_DATE_FORMAT, new Date())) &&
    isMatch(value, DEFAULT_DATE_FORMAT)
  ) {
    return true;
  }

  return false;
};

export const validatePostcode = (value: string, countryCode: string): boolean => {
  if (value === undefined) return false;

  return postcodeValidator(value, countryCode in CountryCode ? countryCode : 'INTL');
};

export const validateOnlyLettersAndSymbols = (value: string) => {
  if (value === undefined || value === null) return false;

  return /^[a-zA-ZÀ-ÿ\s'.-]+$/.test(value);
};

export const validateOnlyLettersAndNumber = (value: string) => {
  if (value === undefined || value === null) return false;

  return /^[a-zA-Z0-9]+$/.test(value);
};

export const validateDateBefore = (startDate: Date, endDate: Date) => {
  return isBefore(startDate, endDate);
};

export const validateFutureDate = (value: string) => {
  if (value === undefined) return false;

  if (isValid(parseISO(value))) {
    return isAfter(parseISO(value), new Date());
  }

  if (isValid(parse(value, DEFAULT_DATE_FORMAT, new Date()))) {
    return isAfter(parse(value, DEFAULT_DATE_FORMAT, new Date()), new Date());
  }

  return false;
};

export const validateSalaryRange = (value: number) => {
  return value >= 0 && value <= 3000000;
};

export const isSameDayButBeforeUTCHour = (value: string, hour: number) => {
  const currentDate = new Date();
  const date = new Date(value);

  if (isSameDay(currentDate, date)) {
    if (getHours(currentDate) < hour) {
      return true;
    }
  }

  return false;
};
